import { useFormContext } from 'react-hook-form';
import { lt } from '@mews/framework';
import { Typography, IconName, Stack, DropDownButton, DropdownItem, Banner, EmptyState } from '@optimus-web/core';
import { ContractTemplateStatus, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { EnterpriseLinkButton, TooltippedHighlightIcon } from '@mews-commander/ui';
import { GenerateContractFormValues } from '../types';
import { FORM_FIELD_PATHS } from '../constants';
import { useCurrentReservation } from '../../hooks/useCurrentReservation';
import { SelectedItemCard } from './SelectedItemCard';

export const AddTemplateSection = () => {
    const serviceId = useCurrentReservation()?.serviceId;
    const { data: contractTemplatesData } = useRepositorySuspenseQuery('reservationContractTemplates', 'getAll', {
        serviceId,
    });
    const {
        setValue,
        watch,
        formState: { errors },
    } = useFormContext<GenerateContractFormValues>();
    const selectedTemplates = watch(FORM_FIELD_PATHS.selectedTemplates);

    const handleSelectedTemplateRemove = (templateId: string) => {
        const { [templateId]: removedTemplate, ...updatedTemplates } = selectedTemplates;
        setValue(FORM_FIELD_PATHS.selectedTemplates, updatedTemplates);
    };

    const handleTemplateSelect = (templateId: string) => {
        setValue(FORM_FIELD_PATHS.selectedTemplates, {
            ...selectedTemplates,
            [templateId]: {
                expirationDate: undefined,
                expirationDateEnabled: false,
            },
        });
    };

    if (contractTemplatesData.templates?.length === 0) {
        return (
            <EmptyState
                primaryText={lt('ContractsGenerateNoTemplatesPrimaryText')}
                secondaryText={lt('ContractsGenerateNoTemplatesSecondaryText')}
                action={
                    <EnterpriseLinkButton to="Settings/ReservationContracts" variant="secondary">
                        {lt('ContractsGenerateTemplateLibraryButton')}
                    </EnterpriseLinkButton>
                }
            />
        );
    }

    return (
        <Stack vertical spacing="200">
            <Stack vertical spacing="100">
                <Typography textStyle="titleMediumStrong" as="h2">
                    {lt('ContractsGenerateContractTemplatesStepLabel')}
                </Typography>
                <Typography textStyle="bodyMedium" as="div">
                    {lt('ContractsGenerateContractTemplatesStepDescription')}
                </Typography>
                {errors.templates?.selectedTemplates?.root && (
                    <Banner variant="warning" primaryText={errors.templates.selectedTemplates.root.message} />
                )}
            </Stack>
            {Object.entries(selectedTemplates).map(([templateId]) => {
                const template = contractTemplatesData.templates?.find((t) => t.id === templateId);
                return (
                    <SelectedItemCard
                        key={templateId}
                        name={template?.name}
                        nameIcons={
                            template?.status === ContractTemplateStatus.NewVersionAvailable ? (
                                <TooltippedHighlightIcon
                                    size="small"
                                    iconVariant="basic"
                                    value={IconName.AlertCircle}
                                    tooltip={lt('ContractTemplateNewVersionAvailableIconTooltip')}
                                />
                            ) : null
                        }
                        expirationDateFieldName={`${FORM_FIELD_PATHS.selectedTemplates}.${templateId}.expirationDate`}
                        expirationDateEnabledFieldName={`${FORM_FIELD_PATHS.selectedTemplates}.${templateId}.expirationDateEnabled`}
                        onRemove={() => handleSelectedTemplateRemove(templateId)}
                    />
                );
            })}
            <Stack distribution="space-around">
                <DropDownButton
                    icon={IconName.PlusSimple}
                    variant="secondary"
                    label={lt('ContractsGenerateContractSelectTemplateButtonLabel')}
                    type="button"
                    disabled={serviceId == null}
                >
                    {contractTemplatesData.templates?.map((template) => (
                        <DropdownItem
                            key={template.id}
                            label={template.name}
                            onClick={() => {
                                handleTemplateSelect(template.id);
                            }}
                            disabled={template.id in selectedTemplates}
                            selected={template.id in selectedTemplates}
                        />
                    ))}
                </DropDownButton>
            </Stack>
        </Stack>
    );
};
